import React, {useCallback, useEffect, useMemo, useState} from "react"
import Grid from "@mui/material/Grid2"
import {DatePicker} from "../RootContainer/DatePicker";
import {DateTime} from "luxon";
import {TimePicker} from "../RootContainer/TimePicker";
import {PrimaryButton} from "../Button";
import {InspectService} from "../../interfaces";
import ReservationActions from "../../Redux/reservationRedux";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../Redux";
import BackButton from "./Common/BackButton";
import {useTranslation} from "react-i18next";

interface SelectInspectionTimeProps {
    campaignCode: string
    service: InspectService
    selectedDate: DateTime
    setSelectedDate: (date: DateTime) => void
    onBack: () => void
}

const SelectInspectionTime: React.FC<SelectInspectionTimeProps> = ({campaignCode, service, selectedDate, setSelectedDate, onBack}) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const [date, setDate] = useState<DateTime>(DateTime.local().plus({days: 1}).startOf('week').startOf('day'))
    const [timeIsSet, setTimeIsSet] = useState(false)

    const chainId = useSelector((state: RootState) => state.ui.settings.chain.id)
    const fetching = useSelector((state: RootState) => state.reservation.fetching)
    const bestPrices = useSelector((state: RootState) => state.reservation.bestPrices)
    const dateTimes = useSelector((state: RootState) => state.reservation.dateTimes)
    const customerInfo = useSelector((state: RootState) => state.ui.customer)
    const registerInfo = useSelector((state: RootState) => state.reservation.registerInfo)

    const fixedPrice = useMemo(() => {
        return registerInfo?.afterInspectionProductId === service?.id ? registerInfo?.afterInspectionPrice?.value : null
    }, [registerInfo, service])

    const dates = useMemo(() => {
        const weekStart = date ? date.startOf('week').startOf('day') : DateTime.local().plus({days: 1}).startOf('week').startOf('day')
        const weekEnd = weekStart.endOf('week').startOf('day')

        let day = weekStart
        const array: DateTime[] = []

        while (day <= weekEnd) {
            // Drop sundays out
            if (day.weekday !== 7) {
                array.push(DateTime.fromISO(day.toISODate()))
            }
            day = day.plus({ days: 1 })
        }
        return array
    }, [date])

    const onGetBestPrices = (
        service: InspectService,
        campaign: string | null,
        customerToken: string | null,
        dateStart: string | undefined,
        dateEnd: string | undefined,
        callback?: () => void,
    ) => {
        if (dateStart && dateEnd)
            dispatch(
                ReservationActions.getBestPrices({
                    chainId,
                    service,
                    campaign,
                    customerToken,
                    dateStart: DateTime.fromISO(dateStart).toISODate(),
                    dateEnd: DateTime.fromISO(dateEnd).toISODate(),
                    callbacks: callback ? [callback] : undefined,
                }),
            )
        else
            dispatch(
                ReservationActions.getBestPrices({
                    chainId,
                    service,
                    campaign,
                    customerToken,
                    dateStart: undefined,
                    dateEnd: undefined,
                    callbacks: callback ? [callback] : undefined,
                }),
            )
    }

    const handleDateChange = useCallback(
        (dateStart: DateTime) => {
            setDate(dateStart)
            setTimeIsSet(false)
        },
        [setDate, setTimeIsSet],
    )

    const handleTimeChange = useCallback((date: DateTime) => {
        const callbacks = []
        callbacks.push(() => setDate(date))
        if (!timeIsSet) callbacks.push(() => setTimeIsSet(true))
        if (chainId) dispatch(ReservationActions.postReservation(date, chainId, undefined, customerInfo?.token, callbacks))
    }, [chainId, customerInfo?.token, dispatch, timeIsSet, setTimeIsSet, setDate]);

    const handleGoBack = () => {
        dispatch(ReservationActions.resetDateTimes())
        if (chainId && timeIsSet) {
            dispatch(ReservationActions.discardReservation(chainId, [onBack]))
        } else {
            onBack()
        }
    }

    const getInspectDateTimes = (
        dateStart: DateTime | null,
        dateEnd: DateTime | null,
        campaign: string | null,
        customerToken: string | null,
        callback?: () => void,
    ) => {
        let callbacks: any
        if (callback) callbacks = [callback]
        if (dateStart) {
            dispatch(ReservationActions.getDateTimes({
                chainId,
                campaign,
                customerToken,
                dateStart: dateStart?.toISODate(),
                dateEnd: dateEnd?.toISODate(),
                callbacks
            }))
        }
    }

    useEffect(() => {
        if (! timeIsSet && selectedDate) {
            handleTimeChange(selectedDate)
        }
        const fromDate = timeIsSet ? date : (selectedDate ?? date)
        onGetBestPrices(
            service,
            campaignCode,
            customerInfo?.token,
            fromDate.startOf('week').toISODate(),
            fromDate.endOf('week').toISODate(),
            () => {
                getInspectDateTimes(
                    fromDate.startOf('week'),
                    fromDate.endOf('week'),
                    campaignCode,
                    customerInfo?.token,
                    undefined
                )
            }
        )
    }, []);

    return (
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={2} className={'pageMainContainer'}>
            <Grid style={{width: '100%'}}>
                <DatePicker
                    forceMobile={false}
                    useSelfServiceTheme
                    prices={bestPrices?.prices}
                    dates={dates}
                    value={date}
                    locale={'fi'}
                    onChange={(newDate) => handleDateChange(newDate)}
                    service={service}
                    additionalFee={service?.additionalFee}
                    fixedPrice={fixedPrice}
                    onGetBestPrices={(
                        service,
                        dateStart,
                        dateEnd,
                        callback,
                    ) => {
                        handleDateChange(
                            dateStart ? DateTime.fromISO(dateStart, {setZone: true}) : date,
                        )
                        onGetBestPrices(
                            service,
                            campaignCode,
                            customerInfo?.token,
                            dateStart,
                            dateEnd,
                            callback,
                        )
                        getInspectDateTimes(
                            dateStart ? DateTime.fromISO(dateStart, {setZone: true}) : null,
                            dateEnd ? DateTime.fromISO(dateEnd, {setZone: true}) : null,
                            campaignCode,
                            customerInfo?.token,
                        )
                    }}
                />
            </Grid>
            <Grid size="grow" style={{width: '100%', marginTop: -8, overflowY: 'auto'}}>
                <TimePicker
                    forceMobile={false}
                    useSelfServiceTheme
                    clickDisabled={fetching.dateTimes || fetching.bestPrices}
                    showPrices={true}
                    timesAndPrices={dateTimes}
                    listPrice={service?.listPrice}
                    additionalFee={service?.additionalFee}
                    fixedPrice={fixedPrice}
                    fetching={fetching.reservation || fetching.checkIn || fetching.dateTimes || fetching.bestPrices}
                    display={!!date}
                    date={date}
                    dates={dates}
                    onChange={handleTimeChange}
                    time={date}
                    first={{ hour: 8, minute: 0 }}
                    last={{ hour: 15, minute: 45 }}
                    timeIsSet={timeIsSet || !! selectedDate}
                    renderMiddleButton={() => (
                        <div>
                        </div>
                    )}
                    renderSelectButton={() => null}
                />
            </Grid>
            <Grid className="footerButtons">
                <Grid container wrap="nowrap" spacing={4} justifyContent="space-between" alignItems="end">
                    <BackButton onBack={handleGoBack} />
                    <PrimaryButton
                        id="next-time"
                        disabled={!date || !timeIsSet || fetching.reservation || fetching.checkIn || fetching.dateTimes || fetching.bestPrices}
                        onClick={() => {setSelectedDate(date)}}
                    >
                        {t('common.continue')}
                    </PrimaryButton>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default SelectInspectionTime
